import React, { useState, useEffect, useMemo } from "react";
import { Table, Form, Button, Spinner, Modal, Dropdown } from "react-bootstrap";
import { toast } from 'react-toastify';
import { GetServiceCaller, PostServiceCaller } from "../../../../services/serviceCaller";
import { API_URL } from "../../../../constants";
import ExportToExcel from "../../../../components/ExportToExcel";
import Pagination from "../../../../components/Pagination";
import SearchBar from "../../../../components/SearchBar";

const FinancialWorkingReport = () => {
    const [data, setData] = useState([]);
    const [currentUserID, setCurrentUserID] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [shiftFilter, setShiftFilter] = useState("ALL"); // New Filter State
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);

    // Confirmation States
    const [showConfirm, setShowConfirm] = useState(false); 
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false); 
    const [submitStep, setSubmitStep] = useState(1); 
    
    const [selectedRows, setSelectedRows] = useState(new Set());
    
    const [visibleColumns, setVisibleColumns] = useState([
        "empId", "empName", "basic", "hra", "lta", "pda", "ca", "nps", "Gross", "pf", "leavecount",
        "totalAnnual", "leaveEncashment","AGross","DGross", "medicalAmount", "ap", "gratuity", "cca", "trai", "revinc", "salesinc", 'acknowledge'
    ]);

    const [sortConfig, setSortConfig] = useState({ key: 'empId', direction: 'asc' });

    // Add this sorting function
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const columns = useMemo(() => ({
        empId: { text: "EMP ID", width: "80px" },
        empName: { text: "Employee Name", width: "150px" },
        Gross: { text: "Gross(APR)", width: "70px" },
        basic: { text: "Basic", width: "70px" },
        hra: { text: "HRA", width: "90px" },
        lta: { text: "LTA", width: "90px" },
        pda: { text: "PDA", width: "90px" },
        ca: { text: "CA", width: "90px" },
        nps: { text: "NPS", width: "90px" },
        AGross: { text: "Gross", width: "130px" },
        DGross: { text: "Delta GROSS", width: "130px" },
        pf: { text: "PF", width: "90px" },
        leavecount:{text:"LC", width: "70px"},
        leaveEncashment: { text: "LWW", width: "90px" },
        medicalAmount: { text: "Medical Amount", width: "140px" },
        ap: { text: "AP", width: "90px" },
        gratuity: { text: "Gratuity", width: "90px" },
        cca: { text: "CCA", width: "90px" },
        trai: { text: "Training Inc.", width: "90px" },
        revinc: { text: "Rev. Inc", width: "90px" },
        salesinc: { text: "Sales Inc.", width: "90px" },
        totalAnnual: { text: "Annual(CTC)", width: "90px" },
        // acknowledge: { text: "Acknowledgement", width: "90px" }
    }), []);

    // Helper function to calculate derived values for a single row
    const calculateDerivedValues = (row) => {
        const updatedRow = { ...row };
        
        // Get numeric values (parse or default to 0)
        const basic = parseFloat(row.basic) || 0;
        const hra = parseFloat(row.hra) || 0;
        const lta = parseFloat(row.lta) || 0;
        const pda = parseFloat(row.pda) || 0;
        const ca = parseFloat(row.ca) || 0;
        const nps = parseFloat(row.nps) || 0;
        const leaveCount = parseFloat(row.leavecount) || 0;
        const grossAPR = parseFloat(row.Gross) || 0;
        
        // 1. Calculate Gross (sum of basic, hra, lta, pda, ca, nps)
        const calculatedGross = basic + hra + lta + pda + ca + nps;
        updatedRow.AGross = calculatedGross;
        
        // 2. Calculate Delta Gross = GrossAPR - Gross
        updatedRow.DGross = grossAPR - calculatedGross;
        
        // 3. Calculate LWW (Leave Encashment) = Basic/30 * LC
        const lww = (basic / 30) * leaveCount;
        updatedRow.leaveEncashment = Math.round(lww);
        
        // 4. Calculate Gratuity = (BASIC * (4.8/100)) * 12
        const gratuity = (basic * (4.8 / 100)) * 12;
        updatedRow.gratuity = Math.round(gratuity);
        
        // 5. Calculate AP = Gross * 1.2 (using calculated Gross)
        // const ap = calculatedGross * 1.2;
        // updatedRow.ap = Math.round(ap);
        
        return updatedRow;
    };

    // Function to update data with calculations
    const updateDataWithCalculations = (prevData, updatedRow) => {
        const calculatedRow = calculateDerivedValues(updatedRow);
        return prevData.map(item => 
            item.id === calculatedRow.id ? calculatedRow : item
        );
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await GetServiceCaller(`${API_URL}get-appraisal-data`);
            if (response.data.status === 'success') {
                setData((response.data.data || []).map((item, i) => ({ 
                    ...item, id: item.id || `row-${i}` 
                })));
                setSelectedRows(new Set()); 
                const userID = response?.data?.userID;
                setCurrentUserID(userID);
            }
        } catch (error) {
            toast.error("Failed to fetch data");
        } finally {
            setTimeout(() => setLoading(false), 300);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = currentTableData.filter(item => item.isSubmit !== 1).map(item => item.id);
            setSelectedRows(new Set(allIds));
        } else {
            setSelectedRows(new Set());
        }
    };

    const handleSelectRow = (id, isSubmit) => {
        if (isSubmit === 1) return;
        const newSelected = new Set(selectedRows);
        if (newSelected.has(id)) { newSelected.delete(id); } else { newSelected.add(id); }
        setSelectedRows(newSelected);
    };

const handleInputChange = (id, field, value) => {
    // Parse the value and ensure it's at least 0
    const numericValue = Math.max(0, parseFloat(value) || 0);

    setData(prevData => {
        const updatedRow = prevData.find(item => item.id === id);
        if (!updatedRow) return prevData;
        
        // Use the validated numericValue instead of the raw value
        const updatedRowWithChange = { ...updatedRow, [field]: numericValue };
        
        // If changing Gross (GrossAPR), we need to recalculate DGross and AGross might also need update
        if (field === 'Gross') {
            const basic = parseFloat(updatedRowWithChange.basic) || 0;
            const hra = parseFloat(updatedRowWithChange.hra) || 0;
            const lta = parseFloat(updatedRowWithChange.lta) || 0;
            const pda = parseFloat(updatedRowWithChange.pda) || 0;
            const ca = parseFloat(updatedRowWithChange.ca) || 0;
            const nps = parseFloat(updatedRowWithChange.nps) || 0;
            const calculatedGross = basic + hra + lta + pda + ca + nps;
            updatedRowWithChange.AGross = calculatedGross;
            updatedRowWithChange.DGross = numericValue - calculatedGross;
            updatedRowWithChange.ap = Math.round(calculatedGross * 1.2);
        } 
         // If changing any component that affects Gross (basic, hra, lta, pda, ca, nps)
        else if (['basic', 'hra', 'lta', 'pda', 'ca', 'nps'].includes(field)) {
            const basic = field === 'basic' ? numericValue : (parseFloat(updatedRowWithChange.basic) || 0);
            const hra = field === 'hra' ? numericValue : (parseFloat(updatedRowWithChange.hra) || 0);
            const lta = field === 'lta' ? numericValue : (parseFloat(updatedRowWithChange.lta) || 0);
            const pda = field === 'pda' ? numericValue : (parseFloat(updatedRowWithChange.pda) || 0);
            const ca = field === 'ca' ? numericValue : (parseFloat(updatedRowWithChange.ca) || 0);
            const nps = field === 'nps' ? numericValue : (parseFloat(updatedRowWithChange.nps) || 0);
            const calculatedGross = basic + hra + lta + pda + ca + nps;
            updatedRowWithChange.AGross = calculatedGross;
            updatedRowWithChange.DGross = (parseFloat(updatedRowWithChange.Gross) || 0) - calculatedGross;
            updatedRowWithChange.ap = Math.round(calculatedGross * 1.2);
            
            if (field === 'basic') {
                const leaveCount = parseFloat(updatedRowWithChange.leavecount) || 0;
                updatedRowWithChange.leaveEncashment = Math.round((basic / 30) * leaveCount);
                updatedRowWithChange.gratuity = Math.round((basic * (4.8 / 100)) * 12);
            }
        }
        // If changing leavecount, recalculate LWW
        else if (field === 'leavecount') {
            const basic = parseFloat(updatedRowWithChange.basic) || 0;
            updatedRowWithChange.leaveEncashment = Math.round((basic / 30) * numericValue);
        }
        // 4. Other fields
        else {
            const basic = parseFloat(updatedRowWithChange.basic) || 0;
            const hra = parseFloat(updatedRowWithChange.hra) || 0;
            const lta = parseFloat(updatedRowWithChange.lta) || 0;
            const pda = parseFloat(updatedRowWithChange.pda) || 0;
            const ca = parseFloat(updatedRowWithChange.ca) || 0;
            const nps = parseFloat(updatedRowWithChange.nps) || 0;
            const calculatedGross = basic + hra + lta + pda + ca + nps;    
            updatedRowWithChange.AGross = calculatedGross;
            updatedRowWithChange.DGross = (parseFloat(updatedRowWithChange.Gross) || 0) - calculatedGross;
            updatedRowWithChange.ap = updatedRowWithChange.ap;
        }
        
        return updateDataWithCalculations(prevData, updatedRowWithChange);
    });
};
    const toggleColumn = (key) => {
        setVisibleColumns(prev => prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]);
    };

    const processData = async (type) => {
        setSaving(true);
        try {
            const submitData = data
                .filter(item => selectedRows.has(item.id))
                .map(item => ({
                    Code: item.empId || "",
                    Name: item.empName || "",
                    Basic_DA: Math.round(parseFloat(item.basic) || 0),
                    HRA: Math.round(parseFloat(item.hra) || 0),
                    SA: Math.round(parseFloat(item.pda) || 0),
                    LTA: Math.round(parseFloat(item.lta) || 0),
                    CA: Math.round(parseFloat(item.ca) || 0),
                    G_SAL: Math.round(parseFloat(item.AGross) || 0),
                    EPF: Math.round(parseFloat(item.pf) || 0),
                    NPS: Math.round(parseFloat(item.nps) || 0),
                    LE_ENC: Math.round(parseFloat(item.leaveEncashment) || 0),
                    CCA: Math.round(parseFloat(item.cca) || 0),
                    TRAI: Math.round(parseFloat(item.trai) || 0),
                    REV_INC: Math.round(parseFloat(item.revinc) || 0),
                    Sales_INC: Math.round(parseFloat(item.salesinc) || 0),
                    AP: Math.round(parseFloat(item.ap) || 0),
                    GT: Math.round(parseFloat(item.gratuity) || 0),
                    MED_INS: Math.round(parseFloat(item.medicalAmount) || 0),
                    DubaiShift: Number(item.dubaiShift) || 0,
                    ActionType: type 
                }));

            const targetEndpoint = type === 'submit' ? `${API_URL}post-temp-employee-payroll-data` : `${API_URL}store-appraisal-data`;
            const response = await PostServiceCaller(targetEndpoint, submitData, "post");
            
            if (response.data.success === 'true' || response.data.status === 'success') {
                toast.success(`Data ${type === 'save' ? 'Saved' : 'Submitted'} Successfully`);
                fetchData();
            }
        } catch (error) {
            toast.error(`${type} failed.`);
        } finally {
            setSaving(false);
        }
    };

    // Updated filteredAndSortedData useMemo to include shiftFilter
    const filteredAndSortedData = useMemo(() => {
        let filtered = data.filter(item => {
            const matchesSearch = item.empId?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
                                item.empName?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const isDubai = Number(item.dubaiShift) === 1;
            const isNonEligible = Number(item.preligibility) === 0;

            if (shiftFilter === "DUBAI") return matchesSearch && isDubai;
            if (shiftFilter === "INDIA") return matchesSearch && !isDubai && !isNonEligible;
            if (shiftFilter === "NON_ELIGIBLE") return matchesSearch && isNonEligible;
            
            return matchesSearch;
        });
        
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                
                if (typeof aVal === 'number' || !isNaN(parseFloat(aVal))) {
                    aVal = parseFloat(aVal) || 0;
                    bVal = parseFloat(bVal) || 0;
                } 
                else {
                    aVal = (aVal || '').toString().toLowerCase();
                    bVal = (bVal || '').toString().toLowerCase();
                }
                
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        
        return filtered;
    }, [data, searchTerm, sortConfig, shiftFilter]);

    // Calculate the separate sums for Gross (APR)
    const totalGrossSums = useMemo(() => {
        return filteredAndSortedData.reduce((acc, item) => {
            const val = parseFloat(item.Gross) || 0;
            if (Number(item.dubaiShift) === 1) {
                acc.dubai += val;
            } else {
                acc.india += val;
            }
            return acc;
        }, { india: 0, dubai: 0 });
    }, [filteredAndSortedData]);

    //Download Excel Dynamically
   const dynamicExportData = useMemo(() => {
    return filteredAndSortedData.map(item => {
        let rowData = {};
        
        visibleColumns.forEach(key => {
            const headerName = columns[key]?.text || key;
            rowData[headerName] = item[key] || 0;
        });

        const annualTotal = 
            (parseFloat(item.Gross) * 12 || 0) + 
            (parseFloat(item.pf) || 0) + 
            (parseFloat(item.leaveEncashment) || 0) + 
            (parseFloat(item.medicalAmount) || 0) + 
            (parseFloat(item.ap) || 0) + 
            (parseFloat(item.gratuity) || 0) + 
            (parseFloat(item.cca) || 0) + 
            (parseFloat(item.trai) || 0) + 
            (parseFloat(item.revinc) || 0) + 
            (parseFloat(item.salesinc) || 0);

        rowData["Annual CTC"] = Math.round(annualTotal);

        return rowData;
    });
}, [filteredAndSortedData, visibleColumns, columns]);


    const currentTableData = useMemo(() => {
        if (recordsPerPage === "ALL") return filteredAndSortedData;
        const start = (currentPage - 1) * parseInt(recordsPerPage);
        return filteredAndSortedData.slice(start, start + parseInt(recordsPerPage));
    }, [currentPage, filteredAndSortedData, recordsPerPage]);

    return (
        <div className="bg-white p-3 shadow-sm rounded" style={{ fontFamily: "'Inter', sans-serif" }}>
            <style>
                {`
                    .custom-table, .btn, .form-control, .form-select, input { font-family: 'Inter', sans-serif !important; }
                    .table-wrapper { position: relative; width: 100%; }
                    .loader-overlay {
                        position: absolute; top: 0; left: 0; right: 0; bottom: 0;
                        background: rgba(255,255,255,0.7); z-index: 1000;
                        display: flex; justify-content: center; align-items: center;
                    }
                    .scroll-container { 
                        width: 100%; overflow-x: auto !important; overflow-y: auto;
                        max-height: 550px; border: 1px solid #dee2e6;
                    }
                    .custom-table {
                        min-width: 1800px; 
                        border-collapse: separate; border-spacing: 0;
                    }
                    .custom-table td, .custom-table th { 
                        padding: 6px 10px !important; border: 1px solid #eee; vertical-align: middle;
                    }
                    .sticky-col-checkbox { position: sticky; left: 0; z-index: 110; width: 40px; min-width: 40px; }
                    .sticky-col-id { position: sticky; left: 40px; z-index: 105; width: 80px; min-width: 80px; }
                    .sticky-col-name { position: sticky; left: 120px; z-index: 105; min-width: 200px; }
                    
                    .scroll-container::-webkit-scrollbar { height: 8px; width: 8px; }
                    .scroll-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
                    .scroll-container::-webkit-scrollbar-thumb { background: #27568b; border-radius: 10px; }
                    .scroll-container::-webkit-scrollbar-thumb:hover { background: #1d3f66; }

                    .editable-cell-input { 
                        border: 1px solid transparent; background: transparent; 
                        width: 100%; text-align: center; font-size: 11px; outline: none;
                    }
                    .editable-cell-input:focus { border-bottom: 1px solid #27568b; background: #fff; }
                    
                    .non-editable-cell { text-align: center; font-size: 11px; padding: 6px 10px; }
                `}
            </style>

            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex gap-2 align-items-center">
                    <div style={{ width: "200px" }}>
                        <SearchBar searchTerm={searchTerm} setSearchTerm={(v) => { setSearchTerm(v); setCurrentPage(1); }} />
                    </div>
                    {/* Shift Filter Dropdown */}
                    <Form.Select 
                        size="sm" 
                        style={{ width: "160px" }} 
                        value={shiftFilter} 
                        onChange={(e) => { setShiftFilter(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="ALL">All Employees</option>
                        <option value="INDIA">India (Eligible)</option>
                        <option value="DUBAI">Dubai Shift</option>
                        <option value="NON_ELIGIBLE">Non-Eligible</option>
                    </Form.Select>
                </div>
                <div className="d-flex gap-2">
                    <Button style={{ backgroundColor: "#27568b", border: "none" }} size="sm" 
                        onClick={() => setShowConfirm(true)} disabled={loading || saving || selectedRows.size === 0}>
                        <i className="bi bi-save me-1"></i> Save
                    </Button>
                    <Button style={{ backgroundColor: "#27568b", border: "none" }} size="sm" 
                        onClick={() => { setSubmitStep(1); setShowSubmitConfirm(true); }} disabled={loading || saving || selectedRows.size === 0 || currentUserID !== 254}>
                        <i className="bi bi-send me-1"></i> Submit
                    </Button>
                    <Dropdown autoClose="outside">
                        <Dropdown.Toggle variant="outline-secondary" size="sm"><i className="bi bi-list"></i></Dropdown.Toggle>
                        <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto", fontSize: "12px" }}>
                            {Object.keys(columns).map(key => (
                                <Form.Check key={key} type="checkbox" label={columns[key].text} checked={visibleColumns.includes(key)} onChange={() => toggleColumn(key)} className="mx-3 my-1" />
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    <ExportToExcel data={dynamicExportData} filename="FinancialReport.csv" />
                    <Button variant="outline-primary" size="sm" onClick={fetchData}><i className="bi bi-arrow-clockwise"></i></Button>
                </div>
            </div>

            <div className="mb-3 p-2 rounded d-flex flex-wrap align-items-center gap-4" style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", fontSize: "11px" }}>
                <div className="d-flex align-items-center gap-2" style={{ fontSize: "12px" }}>
                  <div style={{ width: "14px", height: "14px", backgroundColor: "#CCE5FF", border: "1px solid #b38f2d", borderRadius: "3px" }}></div>
                    <span>Dubai Shift</span>
                </div>
                <div className="d-flex align-items-center gap-2" style={{ fontSize: "12px" }}>
                  <div style={{ width: "14px", height: "14px", backgroundColor: "#ffe5d0", border: "1px solid #b38f2d", borderRadius: "3px" }}></div>
                    <span>Non-Eligible Employees</span>
                </div>
                
                <div style={{ width: "1px", height: "20px", backgroundColor: "#dee2e6" }}></div>

                {/* Separate Sums Display */}
                <div className="d-flex align-items-center gap-3" style={{ fontSize: "12px" }}>
                    <div>
                        <span className="text-muted">India Sum: </span>
                        <span className="fw-bold text-success">
                            ₹{new Intl.NumberFormat('en-IN').format(totalGrossSums.india)}
                        </span>
                    </div>
                    <div>
                        <span className="text-muted">Dubai Sum: </span>
                        <span className="fw-bold text-primary">
                            {new Intl.NumberFormat('en-IN').format(totalGrossSums.dubai)}
                        </span>
                    </div>
                </div>
            </div>

            <div className="table-wrapper">
                {loading && <div className="loader-overlay"><Spinner animation="border" variant="primary" /></div>}
                <div className="scroll-container">
                    <Table hover className="custom-table mb-0 text-nowrap" style={{ fontSize: "11px" }}>
                        <thead>
                            <tr style={{ position: 'sticky', top: 0, zIndex: 120 }}>
                                <th className="sticky-col-checkbox" style={{ backgroundColor: "#27568b", color: "#fff" }}>
                                    <div className="text-center">
                                        <input type="checkbox" onChange={handleSelectAll} checked={currentTableData.length > 0 && currentTableData.filter(i => i.isSubmit !== 1).every(i => selectedRows.has(i.id))} />
                                    </div>
                                </th>
                                {Object.keys(columns).map(key => {
                                    if (!visibleColumns.includes(key)) return null;
                                    let className = (key === 'empId') ? "sticky-col-id" : (key === 'empName') ? "sticky-col-name" : "";
                                     return <th 
                                        key={key} 
                                        className={className} 
                                        style={{ backgroundColor: "#27568b", color: "#fff", textAlign: 'center', minWidth: columns[key].width, cursor: 'pointer' }}
                                        onClick={() => handleSort(key)}
                                    >
                                        {columns[key].text}
                                        {sortConfig.key === key && (
                                            <span className="ms-1">
                                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </th>;
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {currentTableData.map((row) => {
                                const isShiftActive = Number(row.dubaiShift) === 1;
                                const isNoteligibleAPR = Number(row.preligibility) === 0;
                                const rowBg = isNoteligibleAPR ? "#ffe5d0" : selectedRows.has(row.id) ? "#a4dd62" : (isShiftActive ? "#CCE5FF" : "#fff");
                                const annualTotal = (parseFloat(row.Gross)*12 || 0) + (parseFloat(row.pf) || 0) + (parseFloat(row.leaveEncashment) || 0) + 
                                                   (parseFloat(row.medicalAmount) || 0) + (parseFloat(row.ap) || 0) + (parseFloat(row.gratuity) || 0) + 
                                                   (parseFloat(row.cca) || 0) + (parseFloat(row.trai) || 0) + (parseFloat(row.revinc) || 0) + (parseFloat(row.salesinc) || 0);

                                return (
                                    <tr key={row.id}>
                                        <td className="sticky-col-checkbox" style={{ backgroundColor: rowBg }}>
                                            <div className="text-center">
                                                <input type="checkbox" checked={selectedRows.has(row.id)} onChange={() => handleSelectRow(row.id, row.isSubmit)} disabled={row.isSubmit === 1} />
                                            </div>
                                        </td>
                                        {Object.keys(columns).map(key => {
                                            if (!visibleColumns.includes(key)) return null;
                                            let className = (key === 'empId') ? "sticky-col-id" : (key === 'empName') ? "sticky-col-name" : "";
                                            const isEditable = ['basic', 'hra', 'lta', 'pda', 'ca', 'nps', 'pf', 'cca', 'trai', 'revinc', 'salesinc','ap'].includes(key);
                                            const isCalculated = ['AGross', 'DGross', 'ap', 'gratuity'].includes(key);
                                            const isLeaveEncashment = key === 'leaveEncashment';

                                            return (
                                                <td key={key} className={className} style={{ backgroundColor: rowBg, textAlign: 'center', minWidth: columns[key].width }}>
                                                    {key === 'totalAnnual' ? 
                                                        <strong>{annualTotal.toLocaleString()}</strong> : 
                                                        isEditable ? 
                                                            <input 
                                                                className="editable-cell-input" 
                                                                type="number" 
                                                                value={row[key] || 0} 
                                                                onChange={(e) => handleInputChange(row.id, key, e.target.value)} 
                                                                onFocus={(e) => e.target.select()}
                                                            /> : 
                                                            <span className="non-editable-cell">{row[key] || "-"}</span>
                                                    }
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-2">
                    <small className="text-muted">Total: {filteredAndSortedData.length} | Selected: {selectedRows.size}</small>
                    <Form.Select size="sm" style={{ width: "80px" }} value={recordsPerPage} onChange={(e) => { setRecordsPerPage(e.target.value); setCurrentPage(1); }}>
                        <option value="10">10</option><option value="25">25</option><option value="ALL">ALL</option>
                    </Form.Select>
                </div>
                {recordsPerPage !== "ALL" && <Pagination currentPage={currentPage} totalCount={filteredAndSortedData.length} pageSize={parseInt(recordsPerPage)} onPageChange={setCurrentPage} />}
            </div>

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered size="sm">
                <Modal.Body className="p-4 text-center">
                    <h5 className="fw-bold mb-3">Save Changes?</h5>
                    <div className="d-grid gap-2">
                        <Button style={{ backgroundColor: "#27568b", border: "none" }} onClick={() => { setShowConfirm(false); processData('save'); }}>Yes, Save</Button>
                        <Button variant="light" onClick={() => setShowConfirm(false)}>Cancel</Button>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={showSubmitConfirm} onHide={() => setShowSubmitConfirm(false)} centered size="sm">
                <Modal.Body className="p-4 text-center">
                    {submitStep === 1 ? (
                        <>
                            <h5 className="fw-bold mb-3">Review Submission</h5>
                            <div className="d-grid gap-2">
                                <Button style={{ backgroundColor: "#27568b", border: "none" }} onClick={() => setSubmitStep(2)}>Continue</Button>
                                <Button variant="light" onClick={() => setShowSubmitConfirm(false)}>Cancel</Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <h5 className="fw-bold mb-3 text-danger">Final Confirmation</h5>
                            <div className="d-grid gap-2">
                                <Button variant="danger" onClick={() => { setShowSubmitConfirm(false); processData('submit'); }}>Yes, Submit Final</Button>
                                <Button variant="light" onClick={() => setSubmitStep(1)}>Go Back</Button>
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default FinancialWorkingReport;